import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
} from "@mui/material";

import { User } from "../../../types";
import AdminUsersTableRow from "./admin-users-table-row";

type AdminUsersTableProps = {
  usersLoading: boolean;
  filteredUsers: User[];
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
  searchTerm?: string;
  currentUserId: string;
  selectedClientId?: string;
  onBlockClick: (user: User) => void;
};

const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
  usersLoading,
  filteredUsers,
  rowsPerPage,
  currentPage,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
  searchTerm,
  currentUserId,
  selectedClientId,
  onBlockClick,
}) => {
  const maxPage = Math.max(
    0,
    Math.ceil(filteredUsers.length / rowsPerPage) - 1,
  );
  const safePage = Math.min(currentPage, maxPage);
  const paginatedUsers = filteredUsers.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage,
  );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5 }}>
              <TableSortLabel
                active={sortBy === "fullName"}
                direction={sortBy === "fullName" ? sortOrder : "asc"}
                onClick={() => onSort("fullName")}
                sx={{ fontSize: "0.875rem" }}
              >
                User
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, fontSize: "0.875rem" }}>
              Client Role
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, fontSize: "0.875rem" }}>
              Status
            </TableCell>
            <TableCell sx={{ py: 0.5 }}>
              <TableSortLabel
                active={sortBy === "email"}
                direction={sortBy === "email" ? sortOrder : "asc"}
                onClick={() => onSort("email")}
                sx={{ fontSize: "0.875rem" }}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5 }}>
              <TableSortLabel
                active={sortBy === "lastLogin"}
                direction={sortBy === "lastLogin" ? sortOrder : "asc"}
                onClick={() => onSort("lastLogin")}
                sx={{ fontSize: "0.875rem" }}
              >
                Last Login
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, fontSize: "0.875rem" }}>
              Other Clients
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontSize: "0.875rem" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usersLoading ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography>Loading...</Typography>
              </TableCell>
            </TableRow>
          ) : filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary">
                  {searchTerm ? "No users match your search" : "No users found"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedUsers.map((user) => (
              <AdminUsersTableRow
                key={user.userId}
                user={user}
                currentUserId={currentUserId}
                selectedClientId={selectedClientId}
                blockUserClick={onBlockClick}
              />
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredUsers.length}
        page={safePage}
        onPageChange={(_, nextPage) =>
          onPageChange(Math.min(nextPage, maxPage))
        }
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
  );
};

export default AdminUsersTable;
